import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { team as teamService, org as orgService } from 'src/backend';
import { isAssignableFoodRunRecipient } from 'src/utils/foodRunRecipient';
import useAuth from './useAuth';
import { buildRecipientOptions } from 'src/utils/foodRunRecipientMatching';

const finiteCoordinate = (value, fallback) => {
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : fallback;
};

const useOrgsForFoodRunCreation = ({
  lat,
  lng,
  fetchOnMount = true,
  existingDonorTeamId = null,
  existingRecipientTeamId = null,
  ignoreNpoTeamId = null,
  ignoreDonorTeamId = null,
  donorTeamId = null,
}) => {
  const { user } = useAuth();
  const [myRecipients, setMyRecipients] = useState([]);
  const [matchedRecipients, setMatchedRecipients] = useState([]);
  const [matchedRecipientsAreRanked, setMatchedRecipientsAreRanked] =
    useState(false);
  const [donors, setDonors] = useState([]);
  const [existingDonorTeam, setExistingDonorTeam] = useState(null);
  const [existingRecipientTeam, setExistingRecipientTeam] = useState(null);
  const [selfOrg, setSelfOrg] = useState(null);
  const [recipientMatchesLoading, setRecipientMatchesLoading] = useState(false);
  const recipientRequestId = useRef(0);

  const fetchData = useCallback(
    async (otherTeamIdToCheck) => {
      try {
        const [recipientOrgs = [], donorOrgs = []] = await Promise.all([
          teamService.getMyOrgs('RECIPIENT', {
            ignoreAnimalFoodRescuerEnabled: false,
            ignoreTeamId: ignoreNpoTeamId,
            otherTeamIdToCheck: otherTeamIdToCheck ?? donorTeamId,
          }),
          teamService.getMyOrgs('DONOR', { ignoreTeamId: ignoreDonorTeamId }),
        ]);
        setMyRecipients(recipientOrgs.filter(isAssignableFoodRunRecipient));
        setDonors(donorOrgs);

        if (donorOrgs.length === 0) {
          const { orgs = [] } = await orgService.getNearByDonors({
            lat,
            lng,
            ignoreTeamId: ignoreDonorTeamId,
            otherTeamIdToCheck: otherTeamIdToCheck ?? donorTeamId,
            skipPagination: true,
          });
          setDonors(orgs);
        }
      } catch (error) {
        console.error('Error loading food run organizations', error);
      }
    },
    [donorTeamId, ignoreDonorTeamId, ignoreNpoTeamId, lat, lng]
  );

  const fetchRecipientMatches = useCallback(
    async ({ donorLat, donorLng, ...request } = {}) => {
      const requestId = recipientRequestId.current + 1;
      recipientRequestId.current = requestId;
      // Keep the previous ranking on screen until the new one lands so a
      // selected receiver doesn't drop out of the options mid-refresh.
      setRecipientMatchesLoading(true);

      try {
        const result = await orgService.getFoodRunRecipientMatches({
          ...request,
          lat: finiteCoordinate(request.lat ?? donorLat, lat),
          lng: finiteCoordinate(request.lng ?? donorLng, lng),
          donorTeamId: request.donorTeamId ?? donorTeamId,
        });
        if (recipientRequestId.current === requestId) {
          setMatchedRecipients(result.recipients || []);
          setMatchedRecipientsAreRanked(true);
        }
      } finally {
        if (recipientRequestId.current === requestId) {
          setRecipientMatchesLoading(false);
        }
      }
    },
    [donorTeamId, lat, lng]
  );

  const fetchNearByRecipients = useCallback(
    async ({ donorLat, donorLng, ...request } = {}) => {
      recipientRequestId.current += 1;
      setMatchedRecipientsAreRanked(false);
      setRecipientMatchesLoading(false);
      const { orgs = [] } = await orgService.getNearByNPOs({
        ...request,
        lat: finiteCoordinate(donorLat ?? request.lat, lat),
        lng: finiteCoordinate(donorLng ?? request.lng, lng),
        includeSelf: true,
        ignoreTeamId: ignoreNpoTeamId,
        otherTeamIdToCheck:
          request.otherTeamIdToCheck ?? request.donorTeamId ?? donorTeamId,
        skipPagination: true,
      });
      setMatchedRecipients(orgs.filter(isAssignableFoodRunRecipient));
    },
    [donorTeamId, ignoreNpoTeamId, lat, lng]
  );

  useEffect(() => {
    if (fetchOnMount) fetchData();
  }, [fetchData, fetchOnMount]);

  useEffect(() => {
    let active = true;

    if (existingDonorTeamId) {
      orgService.getOrgById({ id: existingDonorTeamId }).then((org) => {
        if (active) setExistingDonorTeam(org);
      });
    } else {
      setExistingDonorTeam(null);
    }

    if (existingRecipientTeamId) {
      orgService.getOrgById({ id: existingRecipientTeamId }).then((org) => {
        if (active) setExistingRecipientTeam(org);
      });
    } else {
      setExistingRecipientTeam(null);
    }

    return () => {
      active = false;
    };
  }, [existingDonorTeamId, existingRecipientTeamId]);

  useEffect(() => {
    let active = true;
    if (user?.team_id && user?.is_non_profit) {
      orgService.getOrgById({ id: user.team_id }).then((org) => {
        if (active) setSelfOrg(org);
      });
    } else {
      setSelfOrg(null);
    }

    return () => {
      active = false;
    };
  }, [user?.team_id, user?.is_non_profit]);

  const totalDonors = useMemo(() => {
    if (!existingDonorTeam?.team_id) return donors;
    if (donors.some((team) => team.team_id === existingDonorTeam.team_id)) {
      return donors;
    }
    return [...donors, existingDonorTeam];
  }, [donors, existingDonorTeam]);

  const totalRecipients = useMemo(
    () =>
      buildRecipientOptions({
        myRecipients,
        matchedRecipients,
        existingRecipient: existingRecipientTeam,
        selfOrg,
        matchedRecipientsAreRanked,
      }),
    [
      existingRecipientTeam,
      matchedRecipients,
      matchedRecipientsAreRanked,
      myRecipients,
      selfOrg,
    ]
  );

  return {
    donors: totalDonors,
    recipients: totalRecipients,
    recipientMatchesLoading,
    invalidateMyOrgs: fetchData,
    isMyRecipientsEmpty: myRecipients.length === 0,
    fetchNearByRecipients,
    fetchRecipientMatches,
  };
};

export default useOrgsForFoodRunCreation;
